import axios from 'axios';
import { READ_ONLY_REASON } from '../v2/viewing';
import { isAiConsentRequired, requestAiConsent } from '../lib/aiConsent';

const API_URL = import.meta.env.VITE_API_URL || '';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // send and receive cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// ONE refresh at a time, shared by the axios interceptor and fetchWithAuth
// (the v2 stream). The backend revokes a refresh token's id when it is used,
// so a second, parallel refresh with the same cookie 401s and lands the user
// on /login (aithlo-frontend#65).
let refreshPromise = null;
export function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = api.post('/auth/v2/token/refresh').finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

const sessionExpired = () => {
  localStorage.removeItem('user');
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
};

// Response interceptor to handle 401 and auto-refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retrying and not an auth endpoint (login/refresh/register)
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/v2/token/refresh') &&
      !originalRequest.url?.includes('/auth/v2/login') &&
      !originalRequest.url?.includes('/auth/v2/register')
    ) {
      originalRequest._retry = true;
      try {
        await refreshSession();          // cookies sent automatically; parallel 401s share it
      } catch (refreshError) {
        sessionExpired();
        return Promise.reject(refreshError);
      }
      return api(originalRequest);       // the new cookie is set
    }

    // The server refused a model call for want of the AI coaching consent
    // (aithlo-backend#64): Layout opens the consent prompt; the caller still
    // sees the refusal (a v2 command words it as "accept the consent first").
    if (isAiConsentRequired(error.response?.status, error.response?.data)) requestAiConsent();

    return Promise.reject(error);
  }
);

// --- Coach-as-athlete viewing ---
let _viewingAthleteId = null;
export const setViewingAthleteId = (id) => { _viewingAthleteId = id; };
export const getViewingAthleteId = () => _viewingAthleteId;

// --- Pending-request tracker (used to show a loading state during coach/athlete switches) ---
let _pendingRequestCount = 0;
const _pendingSubscribers = new Set();
const _notifyPending = () => _pendingSubscribers.forEach((fn) => {
  try { fn(_pendingRequestCount); } catch { /* swallow subscriber errors */ }
});
export const subscribePendingRequests = (fn) => {
  _pendingSubscribers.add(fn);
  return () => _pendingSubscribers.delete(fn);
};
export const getPendingRequestCount = () => _pendingRequestCount;

api.interceptors.request.use((config) => {
  if (_viewingAthleteId) {
    config.params = { ...config.params, athlete_id: _viewingAthleteId };
  }
  _pendingRequestCount += 1;
  _notifyPending();
  return config;
});

api.interceptors.response.use(
  (response) => {
    _pendingRequestCount = Math.max(0, _pendingRequestCount - 1);
    _notifyPending();
    return response;
  },
  (error) => {
    _pendingRequestCount = Math.max(0, _pendingRequestCount - 1);
    _notifyPending();
    return Promise.reject(error);
  }
);

/**
 * Fetch wrapper that handles 401 by refreshing the token and retrying.
 * Used for SSE streaming endpoints that can't go through the axios interceptor.
 */
async function fetchWithAuth(url, options = {}) {
  // Append athlete_id when viewing an athlete's data
  let finalUrl = url;
  if (_viewingAthleteId && !/[?&]athlete_id=/.test(url)) {
    const separator = url.includes('?') ? '&' : '?';
    finalUrl = `${url}${separator}athlete_id=${_viewingAthleteId}`;
  }

  const opts = {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  let response = await fetch(finalUrl, opts);

  if (response.status === 401) {
    try {
      await refreshSession();
    } catch {
      sessionExpired();
      throw new Error('Authentication expired');
    }
    response = await fetch(finalUrl, opts);
  }

  return response;
}

// In-flight dedup helper. When several components fire the same GET at
// app boot (StrictMode double-mount, Calendar + the coach panel both reading
// /profile, Layout + OnboardingContext both reading /profile/onboarding-
// status, etc.), they used to round-trip independently. Concurrent
// callers now share a single Promise — when it resolves the slot clears
// so a real refresh later still goes to the wire.
//
// Keyed per-call so different `viewingAthleteId` values (coach switching
// athletes) don't accidentally share a stale response.
const _inFlight = new Map(); // key -> Promise
const dedupGet = (cacheKey, fetcher) => {
  if (_inFlight.has(cacheKey)) return _inFlight.get(cacheKey);
  const p = fetcher().finally(() => {
    if (_inFlight.get(cacheKey) === p) _inFlight.delete(cacheKey);
  });
  _inFlight.set(cacheKey, p);
  return p;
};

export const profileAPI = {
  getProfile: async () => {
    const key = `profile:${_viewingAthleteId || 'self'}`;
    return dedupGet(key, () => api.get('/profile').then((r) => r.data));
  },
  // Tier + trial context (drives the usage banner and the trial-ended modal).
  getUsage: async () => {
    const { data } = await api.get('/profile/usage');
    return data;
  },
  // Coach's notebook — the athlete-facing view of the coach's memory notes,
  // grouped by kind. Read-only + delete (the coach writes it by chatting).
  getCoachNotebook: async () => {
    const { data } = await api.get('/profile/coach-notebook');
    return data;
  },
  deleteCoachNote: async (id) => {
    const { data } = await api.delete(`/profile/coach-notebook/${id}`);
    return data;
  },
  updateAppearance: async (colors) => {
    const response = await api.put('/profile/appearance', colors);
    return response.data;
  },
  // Recurring weekly schedule (e.g. Tuesday swim club). Backend
  // endpoint /profile/club-sessions is added in the next migration —
  // until then this call resolves to a 404 which the caller swallows.
  updateClubSessions: async (payload) => {
    const response = await api.put('/profile/club-sessions', payload);
    return response.data;
  },
  updateCalendarPreferences: async (prefs) => {
    const response = await api.put('/profile/calendar-preferences', prefs);
    return response.data;
  },
  // ------------------------------------------------------------------
  // Profile update endpoints — must go through this axios `api` instance
  // so the request interceptor can auto-append ?athlete_id when a coach
  // is viewing an athlete. Raw `fetch` bypasses that and writes to the
  // coach's own profile.
  // ------------------------------------------------------------------
  updateBio: async (bio) => {
    const response = await api.put('/profile/bio', { bio });
    return response.data;
  },
  updateMetrics: async (metrics) => {
    const response = await api.put('/profile/metrics', metrics);
    return response.data;
  },
  updateTrainingPreferences: async (prefs) => {
    const response = await api.put('/profile/training-preferences', prefs);
    return response.data;
  },
  updateSportsPreferences: async (prefs) => {
    const response = await api.put('/profile/sports-preferences', prefs);
    return response.data;
  },
  updateInjuries: async (injuries) => {
    const response = await api.put('/profile/injuries', injuries);
    return response.data;
  },
  updateEquipment: async (equipment) => {
    const response = await api.put('/profile/equipment', equipment);
    return response.data;
  },
  updateGoals: async (goals) => {
    const response = await api.put('/profile/goals', goals);
    return response.data;
  },
  // 'coach_led' = AI auto-generates plan; 'self_led' = user drives plan.
  setCoachStatus: async (isCoach) => {
    const response = await api.put('/profile/coach-status', { is_coach: isCoach });
    return response.data;
  },
  updateCoachingMode: async (mode) => {
    const response = await api.put('/profile/coaching-mode', { coaching_mode: mode });
    return response.data;
  },
  // 'pro_tier' | 'ultimate_tier' | 'ultimate_plus_tier' (no free tier; the
  // backend refuses anything else). Picked during onboarding right after
  // username; changeable later from Your plan in Settings.
  updatePricingTier: async (tier) => {
    const response = await api.put('/profile/pricing-tier', { pricing_tier: tier });
    return response.data;
  },
  // How many athlete seats a coach is paying for (min 5). Drives the coach's
  // token budget + the €3/extra-athlete charge.
  updateCoachSeats: async (seats) => {
    const response = await api.put('/profile/coach-seats', { seats });
    return response.data;
  },
  // Opt-in flag — when true, the Strava sync hook appends an
  // attribution string to each new activity's description. Off by
  // default; never set without explicit user action.
  updateStravaCredit: async (enabled) => {
    const response = await api.put('/profile/strava-credit', { enabled });
    return response.data;
  },
  // "Tag matched activities" prefs — master switch + Title/Description
  // sub-toggles. Pass any subset; omitted fields are left unchanged.
  // Default ON. When on, activities matched to a planned session get
  // branded (race legs retitled "{Race} - {Discipline}", description gets
  // the Aithlo tag), written back to Strava on sync for linked users.
  updateActivityTagging: async (prefs) => {
    const response = await api.put('/profile/activity-tagging', prefs);
    return response.data;
  },
  // AI Coins — balance, allotment, next refresh, and per-service toggles.
  getCoins: async () => {
    const response = await api.get('/profile/coins');
    return response.data;
  },
  // Enable/disable one background AI service (no LLM call, no coins when off).
  updateCoinService: async (service, enabled) => {
    const response = await api.put('/profile/coins/service', { service, enabled });
    return response.data;
  },
  // Per-label coin usage breakdown (period: 'day' | 'month' | 'all'). Goes
  // through the axios client so it gets the same 401-refresh as the rest —
  // a raw fetch here would silently 401 on a stale token and render zeros.
  getUsageBreakdown: async (period) => {
    const response = await api.get('/profile/usage/breakdown', { params: { period } });
    return response.data;
  },
  // Fetch a race website + ask GPT to extract structured race info
  // (name, date, distance, terrain, elevation, summary). Used by the
  // Add Race modal to pre-fill the form from a single URL.
  scrapeRaceUrl: async (url) => {
    const response = await api.post('/races/scrape-url', { url });
    return response.data;
  },
  updateUsername: async (username) => {
    const response = await api.put('/profile/username', { username });
    return response.data;
  },
  checkUsernameAvailable: async (username) => {
    const response = await api.get('/profile/username/check', { params: { username } });
    return response.data;
  },
};

export const authAPI = {
  // simple endpoint that will fail with 401 when not logged in
  whoami: async () => {
    const resp = await api.get('/auth/v2/whoami');
    return resp.data;
  },

  // New auth endpoints
  register: async (email, password, dateOfBirth, country) => {
    const response = await api.post('/auth/v2/register', {
      email,
      password,
      date_of_birth: dateOfBirth,
      country,
    });
    return response.data;
  },

  login: async (email, password) => {
    const response = await api.post('/auth/v2/login', { email, password });
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/v2/logout');
    localStorage.removeItem('user');
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/v2/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/v2/reset-password', { token, new_password: newPassword });
    return response.data;
  },

  // Google OAuth - returns the URL to redirect to
  getGoogleLoginUrl: () => {
    const redirectUri = encodeURIComponent(`${window.location.origin}/oauth/callback`);
    return `${API_URL}/auth/v2/google/login?redirect_uri=${redirectUri}`;
  },
};

export const activitiesAPI = {
  // Off the calendar, load and coach's view; kept, and it stays gone across syncs (backend app/activities/removal.py).
  removeActivity: async (activityId) => (await api.delete(`/activities/${activityId}`)).data,
  restoreActivity: async (activityId) => (await api.post(`/activities/${activityId}/restore`)).data,
  // Every source's version of one activity (Strava, Garmin, Shift, …) and which one it shows (backend app/activities/copies.py).
  getCopies: async (activityId) => (await api.get(`/activities/${activityId}/copies`)).data,
  // Show this copy's values (null: back to the athlete's source order).
  chooseCopy: async (activityId, copyId) => (await api.post(`/activities/${activityId}/copies/choose`, { copy_id: copyId })).data,
  getUserActivities: async (params = {}) => {
    const response = await api.get('/activities/user', { params });
    return response.data;
  },

  // Aggregate stats incl. per-type avg_speed (m/s) in `by_type`. Optional
  // start_date/end_date narrow the window; omit for all-time.
  getStatsSummary: async (params = {}) => {
    const response = await api.get('/activities/stats/summary', { params });
    return response.data;
  },

  toggleStar: async (activityId) => {
    const response = await api.patch(`/activities/${activityId}/star`);
    return response.data;
  },

  // Merge ≥2 same-type activities into one activity (Aithlo + Strava).
  // Uploads a combined activity to Strava and hides the originals in Aithlo;
  // the originals REMAIN on Strava (no delete API) for manual cleanup.
  mergeActivities: async ({ activity_ids, name }) => {
    const response = await api.post('/activities/merge', { activity_ids, name });
    return response.data;
  },

  // Optional second step: upload a merged activity to Strava. 409s while any
  // of the originals still exists there (Strava rejects it as a duplicate).
  pushMergeToStrava: async (activityId) => {
    const response = await api.post(`/activities/${activityId}/push-to-strava`);
    return response.data;
  },

  getMergeSources: async (activityId) => {
    const response = await api.get(`/activities/${activityId}/merge-sources`);
    return response.data;
  },

  // Timed KOM / sprint efforts on a Shift ride: { efforts: [...] }. Empty for
  // anything that isn't a Shift ride.
  getSegments: async (activityId) => {
    const response = await api.get(`/activities/${activityId}/segments`);
    return response.data;
  },

  // Split a merged activity back into the activities it was built from.
  // Restores them from the copies kept at merge time, so it works even after
  // the originals have been deleted on Strava.
  unmergeActivity: async (activityId) => {
    const response = await api.post(`/activities/${activityId}/unmerge`);
    return response.data;
  },

  uploadToStrava: async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/activities/upload-to-strava', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
    return response.data;
  },

  // Parse a GPX file server-side and return aggregate metrics so the
  // manual-session modal can pre-fill distance / duration / elevation /
  // HR. The file is not persisted — purely a stateless parser.
  parseGpx: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/activities/parse-gpx', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export const segmentsAPI = {
  // The athlete's starred Strava segments — the primary picker (Strava has no
  // free-text segment search).
  getStarred: async (params = {}) => {
    const response = await api.get('/segments/starred', { params });
    return response.data.segments;
  },

  // Geographic discovery: bounds = "sw_lat,sw_lng,ne_lat,ne_lng".
  explore: async (bounds, activityType = 'riding') => {
    const response = await api.get('/segments/explore', {
      params: { bounds, activity_type: activityType },
    });
    return response.data.segments;
  },

  // Cached segment detail (athlete-stripped).
  getSegment: async (segmentId) => {
    const response = await api.get(`/segments/${segmentId}`);
    return response.data;
  },

  // Elevation-over-distance profile { segment, distance[], altitude[], latlng[] }.
  getProfile: async (segmentId) => {
    const response = await api.get(`/segments/${segmentId}/profile`);
    return response.data;
  },

  // The athlete's real PR (time+power) + KOM/QOM times, for the comparison
  // and to prefill a target time.
  getBenchmarks: async (segmentId) => {
    const response = await api.get(`/segments/${segmentId}/benchmarks`);
    return response.data;
  },

  // Rider bike/position physics (CdA, Crr, bike weight) — used instead of
  // defaults so predictions match the athlete's real setup.
  getBikeSetup: async () => {
    const response = await api.get('/segments/bike-setup');
    return response.data;
  },
  saveBikeSetup: async (setup) => {
    const response = await api.put('/segments/bike-setup', setup);
    return response.data;
  },

  // Per-section optimal target watts. Pass { target_time_s } to hit a goal time,
  // or { effort } ∈ 'pr' | 'hard' | 'tempo'. A 422 { detail: 'no_power_model' }
  // means no FTP/CP yet — prompt the athlete to set FTP.
  getPowerPlan: async (segmentId, opts = {}) => {
    const body = opts.target_time_s != null ? { target_time_s: opts.target_time_s } : { effort: opts.effort || 'hard' };
    const response = await api.post(`/segments/${segmentId}/power-plan`, body);
    return response.data;
  },

  // The same plan as a Garmin/Wahoo .fit COURSE (blob) — each section becomes a
  // course point named with its target watts, so the head unit announces the
  // number on the road with no Connect IQ app. Same options as getPowerPlan.
  downloadCourseFit: async (segmentId, opts = {}) => {
    const params = opts.target_time_s != null
      ? { target_time_s: opts.target_time_s }
      : { effort: opts.effort || 'hard' };
    const response = await api.get(`/segments/${segmentId}/course.fit`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

export const routesAPI = {
  // List public catalogue + the caller's personal routes.
  list: async () => {
    const response = await api.get('/routes');
    return response.data;
  },

  // Fetch a single route with its full route_data payload.
  get: async (routeId) => {
    const response = await api.get(`/routes/${routeId}`);
    return response.data;
  },

  // Admin-only: create a new public route from the React generator.
  // `payload` shape: { name, description?, activity_type?, route_data }.
  createPublic: async (payload) => {
    const response = await api.post('/admin/routes', payload);
    return response.data;
  },

  // Admin-only: update an existing public route. Sentinel-aware —
  // omit fields you don't want to change. Passing route_data also
  // refreshes the cached summary stats backend-side.
  updatePublic: async (routeId, patch) => {
    const response = await api.put(`/admin/routes/${routeId}`, patch);
    return response.data;
  },

  // Admin-only: soft-delete (is_active = false).
  deletePublic: async (routeId) => {
    const response = await api.delete(`/admin/routes/${routeId}`);
    return response.data;
  },
};

export const onboardingAPI = {
  // Get onboarding status — deduped: Layout + OnboardingContext both
  // call this on mount and StrictMode doubles each one in dev.
  getStatus: async () => {
    const key = `onboarding-status:${_viewingAthleteId || 'self'}`;
    return dedupGet(key, () => api.get('/profile/onboarding-status').then((r) => r.data));
  },

  // Update progress for a step. `step` is the step NAME (one of STEP_IDS in
  // lib/onboardingSteps.js) — the backend stores names, not indices.
  updateProgress: async (step, completed = true) => {
    const response = await api.put('/profile/onboarding-progress', { step, completed });
    return response.data;
  },

  // Mark onboarding as complete
  complete: async () => {
    const response = await api.post('/profile/onboarding-complete');
    return response.data;
  },
};

export const workoutsAPI = {
  // Get all workouts for the viewable user (athlete in coach-view,
  // self otherwise). This is the default callers expect.
  getAllWorkouts: async () => {
    const response = await api.get('/workouts');
    return response.data;
  },

  // Get workouts for the *authenticated caller* regardless of
  // athlete-view state. Used by the Workouts library page so a coach
  // managing an athlete still sees their own library.
  getMyWorkouts: async () => {
    const response = await api.get('/workouts', { params: { scope: 'caller' } });
    return response.data;
  },

  // Flip the per-workout coach→athlete visibility flag.
  setShared: async (workoutId, isShared) => {
    const response = await api.patch(`/workouts/${workoutId}/share`, {
      is_shared_with_athletes: isShared,
    });
    return response.data;
  },

  // Get public workout library
  getPublicWorkouts: async () => {
    const response = await api.get('/public-workouts');
    return response.data;
  },

  // Get single workout
  getWorkout: async (workoutId) => {
    const response = await api.get(`/workouts/${workoutId}`);
    return response.data;
  },

  // Create new workout
  createWorkout: async (data) => {
    const response = await api.post('/workouts', data);
    return response.data;
  },

  // Update workout
  updateWorkout: async (workoutId, data) => {
    const response = await api.put(`/workouts/${workoutId}`, data);
    return response.data;
  },

  // Delete workout
  deleteWorkout: async (workoutId) => {
    const response = await api.delete(`/workouts/${workoutId}`);
    return response.data;
  },

  // Generate workout with AI and save it to the athlete's Workouts library.
  // Only for an explicit "generate into my library" action; anything that drops
  // the result into an editor or a calendar session uses previewWorkout.
  generateWorkout: async (durationMinutes, workoutType, notes = null, sport = 'ride') => {
    const response = await api.post('/workouts/generate', {
      duration_minutes: durationMinutes,
      workout_type: workoutType,
      notes: notes,
      sport: sport,
    });
    return response.data;
  },

  // Preview/generate workout with AI without saving to library
  previewWorkout: async (durationMinutes, workoutType, notes = null, sport = 'ride', cues = true) => {
    const response = await api.post('/workouts/generate', {
      cues,
      duration_minutes: durationMinutes,
      workout_type: workoutType,
      notes: notes,
      sport: sport,
      save: false, // Nothing is saved: the structure (plus a ride's cues/shift) comes back
    });
    return response.data;
  },

  // The generator's workout types per sport, from the coach's sport skills:
  // { types: { bike: [...], run: [...], swim: [...], gym: [...] }, surprise_me }.
  // Read once per session via lib/useWorkoutTypes.js.
  getGenerateTypes: async () => {
    const response = await api.get('/workouts/generate/types');
    return response.data;
  },

  // Download workout as FIT file
  downloadWorkoutFit: async (workoutId) => {
    const response = await api.get(`/workouts/${workoutId}/fit`, { responseType: 'blob' });
    return response.data;
  },

  // Where this athlete can send a workout: { push: [{provider, label, sports}], fit_download }.
  // `push` lists only providers that can take a pushed workout right now.
  getSendTargets: async () => {
    const response = await api.get('/workouts/send-targets');
    return response.data;
  },

  // Schedule a library workout (own or public) on the athlete's watch calendar.
  // `date` YYYY-MM-DD or null (the athlete's today). Doesn't touch the Aithlo plan.
  sendWorkout: async (workoutId, provider, date = null) => {
    const response = await api.post(`/workouts/${encodeURIComponent(workoutId)}/send`, { provider, date });
    return response.data;
  },

  // Get today's plan workouts (bike sessions from training plan)
  getTodaysPlanWorkouts: async () => {
    const response = await api.get('/workouts/today-plan');
    return response.data;
  },

  // Get all planned workouts for the next 2 weeks, grouped by date
  getPlannedWorkouts: async () => {
    const response = await api.get('/workouts/planned');
    return response.data;
  },
};

export const accountsAPI = {
  // Which source's values an activity shows when several have it: { order: [{source, name, has_activities}], custom }.
  getRemovedActivities: async () => (await api.get('/accounts/removed-activities')).data,
  getActivitySourceOrder: async () => (await api.get('/accounts/activity-source-order')).data,
  setActivitySourceOrder: async (sources) => (await api.put('/accounts/activity-source-order', { sources })).data,
  // Which device's sleep/HRV/resting HR the coach reads when several record them: { order: [{source, name, has_data}], custom }.
  getRecoverySourceOrder: async () => (await api.get('/accounts/recovery-source-order')).data,
  setRecoverySourceOrder: async (sources) => (await api.put('/accounts/recovery-source-order', { sources })).data,
  // Get all linked accounts
  getLinkedAccounts: async () => {
    const response = await api.get('/accounts');
    return response.data;
  },

  // Every data provider (Strava, Garmin, COROS, Wahoo, Apple Health) with this
  // athlete's status, what it provides, the consents its connect needs and its
  // history load. Settings and onboarding render from it.
  getProviders: async () => {
    const response = await api.get('/accounts/providers');
    return response.data.providers || [];
  },

  // A provider's OAuth URL, fetched through axios (format=json) so a missing
  // consent comes back as the 403 the interceptor turns into the consent
  // prompt, rather than a raw error page. The caller then navigates to it.
  getConnectUrl: async (connectPath, { onboarding = false, redirectUri = null } = {}) => {
    const response = await api.get(connectPath, {
      params: { format: 'json', ...(onboarding ? { onboarding: 'true' } : {}),
                ...(redirectUri ? { redirect_uri: redirectUri } : {}) },
    });
    return response.data.url;
  },

  // Disconnect a provider (its disconnect_path): deletes its data with it.
  disconnectProvider: async (disconnectPath) => {
    const response = await api.delete(disconnectPath);
    return response.data;
  },

  // Get Strava connect URL (redirects to Strava OAuth).
  // `onboarding=true` flags this as part of the onboarding flow so the
  // backend's callback includes &onboarding=true in the return URL.
  // `redirectUri` lets the caller pick the page Strava bounces us back
  // to — pass a non-proxied SPA path (e.g. /calendar) so the Vite dev
  // proxy doesn't grab a page navigation to /profile and return JSON.
  getStravaConnectUrl: (onboarding = false, redirectUri = null) => {
    const params = new URLSearchParams();
    if (onboarding) params.set('onboarding', 'true');
    if (redirectUri) params.set('redirect_uri', redirectUri);
    const qs = params.toString();
    return `${API_URL}/accounts/strava/connect${qs ? `?${qs}` : ''}`;
  },

  // Disconnect Strava (deletes account link and all activities)
  disconnectStrava: async () => {
    const response = await api.delete('/accounts/strava');
    return response.data;
  },

  // Permanently delete the user's account and ALL their data.
  // `confirm` must equal the user's email address; the backend rejects otherwise.
  deleteAccount: async (confirm) => {
    const response = await api.post('/accounts/me/delete', { confirm });
    return response.data;
  },

  // Export Strava activities as CSV
  exportStravaData: async () => {
    const response = await api.get('/accounts/strava/export', {
      responseType: 'blob',
    });
    return response.data;
  },

  // Trigger manual Strava sync
  syncStrava: async () => {
    const response = await api.post('/accounts/strava/sync');
    return response.data;
  },

  // Poll whether the post-connect Strava import is still running. Onboarding
  // blocks the interview until syncing is false so the coach reads real data.
  getSyncStatus: async () => {
    const response = await api.get('/accounts/strava/sync-status');
    return response.data; // { connected, syncing, activity_count }
  },
};

export const consentsAPI = {
  // Deduped: Layout's effect fires twice in StrictMode dev mode.
  getStatus: async () => {
    const key = `consents-status:${_viewingAthleteId || 'self'}`;
    return dedupGet(key, () => api.get('/consents/status').then((r) => r.data));
  },

  record: async (consentType, version) => {
    const response = await api.post('/consents', {
      consent_type: consentType,
      version,
    });
    return response.data;
  },

  // Several at once, in one transaction (sign-up, "We've updated our terms"):
  // `consents` is [{consent_type, version}]; `confirmAdult` also records 18+.
  // 400 on an unknown type or a version that isn't current.
  recordBatch: async (consents, confirmAdult = false) => {
    const response = await api.post('/consents/batch', {
      consents,
      confirm_adult: Boolean(confirmAdult),
    });
    return response.data;
  },
};

export const STRAVA_DATA_RETENTION_CONSENT = {
  type: 'strava_data_retention',
  version: 'v1',
};

export const statsAPI = {
  // Get current training load (ATL, CTL, TSB)
  getTrainingLoad: async () => {
    const response = await api.get('/stats/training-load');
    return response.data;
  },

  // Get training load history for charting
  getTrainingLoadHistory: async (days = 90) => {
    const response = await api.get(`/stats/training-load/history?days=${days}`);
    return response.data;
  },

  // Summary cards (7d, 30d, year, longest ever)
  getSummaryCards: async () => {
    const response = await api.get('/stats/summary-cards');
    return response.data;
  },

  // Run pace trends (avg speed + pace per week)
  getPaceTrends: async (weeks = 12) => {
    const response = await api.get(`/stats/pace-trends?weeks=${weeks}`);
    return response.data;
  },

  // Weekly hours broken down by sport type
  getWeeklySportBreakdown: async (weeks = 12) => {
    const response = await api.get(`/stats/weekly-sport-breakdown?weeks=${weeks}`);
    return response.data;
  },

  // Weekly elevation gain
  getElevationTrends: async (weeks = 12) => {
    const response = await api.get(`/stats/elevation-trends?weeks=${weeks}`);
    return response.data;
  },

  // Consistency score (% of weeks with 3+ sessions)
  getConsistencyScore: async (weeks = 12) => {
    const response = await api.get(`/stats/consistency-score?weeks=${weeks}`);
    return response.data;
  },

  // Personal bests (longest distance, time, elevation, speed, HR)
  getPersonalBests: async () => {
    const response = await api.get('/activities/stats/personal-bests');
    return response.data;
  },

  // Monthly summary (hours + activity count per month)
  getMonthlySummary: async (months = 12) => {
    const response = await api.get(`/stats/monthly-summary?months=${months}`);
    return response.data;
  },

  // Weekly duration (hours per week)
  getWeeklyDuration: async (weeks = 12) => {
    const response = await api.get(`/stats/weekly-duration?weeks=${weeks}`);
    return response.data;
  },

  // Daily AI "how you're getting on" Home check-in (Pro+; cached server-side per day)
  getCoachBrief: async ({ refresh = false } = {}) => {
    const response = await api.get(`/stats/coach-brief${refresh ? '?refresh=true' : ''}`);
    return response.data; // { brief: string|null, cached: bool }
  },

  // ── Stream-derived performance analytics + on-demand streams backfill ──
  // Backfill coverage + live progress ({ total, with_streams, missing, running, progress, rate })
  getStreamsStatus: async () => {
    const response = await api.get('/stats/streams/status');
    return response.data;
  },
  // Kick (or report an already-running) on-demand streams backfill for this athlete
  startStreamsBackfill: async () => {
    const response = await api.post('/stats/streams/backfill');
    return response.data;
  },
  // Best-power duration curve (ride) — { durations, all_time, recent, meta }
  getPowerCurve: async ({ sport = 'ride', days = 365, recentWeeks = 6 } = {}) => {
    const response = await api.get(
      `/stats/power-curve?sport=${sport}&days=${days}&recent_weeks=${recentWeeks}`
    );
    return response.data;
  },
  // Best-speed/pace duration curve (run) — { durations, *_speed_ms, *_pace_min_km, meta }
  getPaceCurve: async ({ sport = 'run', days = 365, recentWeeks = 6 } = {}) => {
    const response = await api.get(
      `/stats/pace-curve?sport=${sport}&days=${days}&recent_weeks=${recentWeeks}`
    );
    return response.data;
  },
  // Critical Power / FTP estimate from the curve (ride) — { cp, w_prime, ftp_estimate, points }
  getCpEstimate: async ({ sport = 'ride', days = 365 } = {}) => {
    const response = await api.get(`/stats/cp-estimate?sport=${sport}&days=${days}`);
    return response.data;
  },
  // Predicted race times from the run pace curve — { predictions: [{race,distance_m,seconds,time}], exponent, model }
  getRacePredictions: async ({ days = 365 } = {}) => {
    const response = await api.get(`/stats/race-predictions?days=${days}`);
    return response.data;
  },
  // Aerobic decoupling trend — { points: [{ date, decoupling_pct, name, ... }] }
  getDecoupling: async ({ sport = 'run', weeks = 26, minMinutes = 20 } = {}) => {
    const response = await api.get(
      `/stats/decoupling?sport=${sport}&weeks=${weeks}&min_minutes=${minMinutes}`
    );
    return response.data;
  },
  // Efficiency Factor trend — { points: [{ date, ef, name, ... }] }
  getEfficiencyFactor: async ({ sport = 'run', weeks = 26 } = {}) => {
    const response = await api.get(`/stats/efficiency-factor?sport=${sport}&weeks=${weeks}`);
    return response.data;
  },
};

export const shiftSocialAPI = {
  // This week's Shift segment challenge: { challenge, top, my_best, riders }.
  // `week` (YYYY-MM-DD) picks another week; omit for the current one.
  getWeeklyChallenge: async (week) => {
    const response = await api.get('/shift/challenges/weekly', { params: week ? { week } : undefined });
    return response.data;
  },

  // Friends
  getFriends: async () => {
    const response = await api.get('/shift/friends');
    return response.data;
  },

  addFriend: async (username) => {
    const response = await api.post('/shift/friends/add', { username });
    return response.data;
  },

  acceptFriend: async (friendId) => {
    const response = await api.post(`/shift/friends/${friendId}/accept`);
    return response.data;
  },

  removeFriend: async (friendId) => {
    const response = await api.delete(`/shift/friends/${friendId}`);
    return response.data;
  },

  // Scheduled Rides
  getScheduledRides: async () => {
    const response = await api.get('/shift/rides');
    return response.data;
  },

  scheduleRide: async (routeId, workoutId, scheduledTime, notes, inviteAllFriends = true, invitedUserIds = null, rideMode = "normal") => {
    const response = await api.post('/shift/rides/schedule', {
      route_id: routeId,
      workout_id: workoutId,
      scheduled_time: scheduledTime,
      notes: notes,
      invite_all_friends: inviteAllFriends,
      invited_user_ids: invitedUserIds,
      ride_mode: rideMode,
    });
    return response.data;
  },

  joinRide: async (rideId) => {
    const response = await api.post(`/shift/rides/${rideId}/join`);
    return response.data;
  },

  leaveRide: async (rideId) => {
    const response = await api.post(`/shift/rides/${rideId}/leave`);
    return response.data;
  },

  cancelRide: async (rideId) => {
    const response = await api.delete(`/shift/rides/${rideId}`);
    return response.data;
  },
};

export const planAPI = {
  // The calendar's rows (optionally filtered by date range). Every calendar
  // write is a v2 command (src/v2/commands.js), not a /plan write.
  getPlan: async (startDate, endDate) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/plan', { params });
    return response.data;
  },

  // Stretch Buddy — generate a ~20-min stretch routine tailored
  // to whatever the athlete did on `date` (YYYY-MM-DD). Always
  // regenerates server-side, with token usage logged against the
  // user's tier budget.
  generateStretchBuddy: async (date) => {
    const response = await api.post('/plan/stretch-buddy', { date });
    return response.data;
  },

  // Print-ready HTML for one session (aithlo-mobile#37). Fetched through the
  // authed client rather than opening the URL directly, so it works whatever
  // the cookie domain is — the window is filled from the response.
  getSessionPrintHtml: async (date, sessionIndex, id = null) => {
    const { data } = await api.get('/plan/session/print', {
      params: id != null ? { date, id } : { date, session_index: sessionIndex },
      responseType: 'text',
    });
    return data;
  },

  // Export a planned session as a structured .fit file (blob). Only works for
  // sessions that have a coach-planned workout structure (backend returns 422
  // otherwise). Available to every tier.
  exportSessionFit: async (date, sessionIndex, id = null) => {
    const response = await api.get('/plan/session/fit', {
      params: id != null ? { date, id } : { date, session_index: sessionIndex },
      responseType: 'blob',
    });
    return response.data;
  },

  // The athlete's earlier "not this session" choices (Strava id ↔ day), read
  // alongside the v2 store's links (calendarRoutes.athleteLinks).
  getDisconnections: async () => {
    const response = await api.get('/plan/disconnections');
    return response.data;
  },
};

export const shiftActivitiesAPI = {
  getActivities: async () => {
    const response = await api.get('/shift/activities');
    return response.data;
  },

  getActivity: async (activityId) => {
    const response = await api.get(`/shift/activities/${activityId}`);
    return response.data;
  },

  downloadFit: async (activityId) => {
    const response = await api.get(`/shift/activities/${activityId}/fit`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Level, XP, weekly streak, totals, medals and best workout grades, worked out
  // from the rider's Shift rides (backend app/shift/progress.py). The game reads
  // the same endpoint.
  getProgress: async () => {
    const response = await api.get('/unity/auth/progress');
    return response.data;
  },
};

export const coachingAPI = {
  getRelationships: async () => {
    const response = await api.get('/coaching/relationships');
    return response.data;
  },
  sendRequest: async (username, role) => {
    const response = await api.post('/coaching/request', { username, role });
    return response.data;
  },
  acceptRequest: async (id) => {
    const response = await api.post(`/coaching/${id}/accept`);
    return response.data;
  },
  removeRelationship: async (id) => {
    const response = await api.delete(`/coaching/${id}`);
    return response.data;
  },
  searchCoaches: async (q) => {
    const response = await api.get('/coaching/search', { params: { q } });
    return response.data;
  },
  searchAthletes: async (q) => {
    const response = await api.get('/coaching/search-athletes', { params: { q } });
    return response.data;
  },
  getMessages: async (relId, limit = 50, beforeId = null) => {
    const params = { limit };
    if (beforeId) params.before_id = beforeId;
    const response = await api.get(`/coaching/messages/${relId}`, { params });
    return response.data;
  },
  sendMessage: async (relId, content) => {
    const response = await api.post(`/coaching/messages/${relId}`, { content });
    return response.data;
  },
  markRead: async (relId) => {
    const response = await api.post(`/coaching/messages/${relId}/read`);
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await api.get('/coaching/unread-count');
    return response.data;
  },
  toggleCoach: async () => {
    const response = await api.put('/coaching/toggle-coach');
    return response.data;
  },
};

// Admin AI-spend view. Reports COINS (the enforced budget) inside each
// athlete's own billing cycle — not the token totals adminAPI/Admin.jsx show.
export const adminAIUsageAPI = {
  getUsage: async (params = {}) => {
    const response = await api.get('/admin/ai-usage', { params });
    return response.data;
  },
  getAthlete: async (userId) => {
    const response = await api.get(`/admin/ai-usage/${userId}`);
    return response.data;
  },
};

// The admin coach console (backend app/admin/routes_coach.py): what the v2 coach
// did and what it cost. All GET, admin only. Empty / null / false filters are
// left off so the backend's own defaults (limits included) apply.
const coachParams = (params = {}) => Object.fromEntries(
  Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== false),
);

export const adminCoachAPI = {
  getOverview: async () => {
    const response = await api.get('/admin/coach/overview');
    return response.data;
  },
  // { real_only }
  getAthletes: async (params = {}) => {
    const response = await api.get('/admin/coach/athletes', { params: coachParams(params) });
    return response.data;
  },
  // { user_id, outcome, limit }
  getRuns: async (params = {}) => {
    const response = await api.get('/admin/coach/runs', { params: coachParams(params) });
    return response.data;
  },
  getRun: async (runId) => {
    const response = await api.get(`/admin/coach/runs/${encodeURIComponent(runId)}`);
    return response.data;
  },
  // { user_id, skill, errors_only, limit }
  getModelCalls: async (params = {}) => {
    const response = await api.get('/admin/coach/model-calls', { params: coachParams(params) });
    return response.data;
  },
  getModelCall: async (callId) => {
    const response = await api.get(`/admin/coach/model-calls/${encodeURIComponent(callId)}`);
    return response.data;
  },
  // { user_id, set_name, backend, limit }
  getDecideCalls: async (params = {}) => {
    const response = await api.get('/admin/coach/decide-calls', { params: coachParams(params) });
    return response.data;
  },
  // { user_id, limit }
  getPlanReviews: async (params = {}) => {
    const response = await api.get('/admin/coach/plan-reviews', { params: coachParams(params) });
    return response.data;
  },
};

export const adminUsersAPI = {
  getUsers: async (params = {}) => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  getUserDetail: async (userId) => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },

  updateUsername: async (userId, username) => {
    const response = await api.put(`/admin/users/${userId}/username`, { username });
    return response.data;
  },

  updateUserTier: async (userId, tier) => {
    const response = await api.put(`/admin/users/${userId}/tier`, { tier });
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },

  assignCoach: async (userId, coachUsername) => {
    const response = await api.post(`/admin/users/${userId}/assign-coach`, { coach_username: coachUsername });
    return response.data;
  },

  resetPassword: async (userId, newPassword) => {
    const response = await api.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword });
    return response.data;
  },

  // Wipe a user's data (activities, plans, workouts, AI history) + disconnect
  // Strava so they re-onboard. Keeps account/login/social. `confirm` must echo
  // the user's email.
  resetUserData: async (userId, confirm) => {
    const response = await api.post(`/admin/users/${userId}/reset-data`, { confirm });
    return response.data;
  },

  // Delete ALL planned sessions for a user (training plan calendar + phases +
  // any draft) and clear the last-generated stamp so a fresh plan can be built.
  // Keeps activities, profile, workouts and chat.
  deletePlan: async (userId) => {
    const response = await api.post(`/admin/users/${userId}/delete-plan`, {});
    return response.data;
  },

  // Admin onboarding override. payload: { action?: "reset"|"complete", coaching_mode?: "coach_led"|"self_led" }
  updateOnboarding: async (userId, payload) => {
    const response = await api.put(`/admin/users/${userId}/onboarding`, payload);
    return response.data;
  },

  getAllowlist: async () => {
    const response = await api.get('/admin/allowlist');
    return response.data;
  },

  addToAllowlist: async (email) => {
    const response = await api.post('/admin/allowlist', { email });
    return response.data;
  },

  removeFromAllowlist: async (email) => {
    const response = await api.delete(`/admin/allowlist/${encodeURIComponent(email)}`);
    return response.data;
  },
};

// Placeholder billing (no real charges) — fake invoice + coach codes.
export const billingAPI = {
  getSummary: async () => {
    const response = await api.get('/billing/summary');
    return response.data;
  },
  // A coach's shareable code (Ultimate tiers only; 402 otherwise).
  getCoachCode: async () => {
    const response = await api.get('/billing/coach-code');
    return response.data; // { code }
  },
  // Redeem a coach code → half-price Pro for the athlete.
  redeemCode: async (code) => {
    const response = await api.post('/billing/redeem-code', { code });
    return response.data; // { message, billing }
  },
  // Start the one-time Pro trial (Free accounts that haven't used it).
  startTrial: async () => {
    const response = await api.post('/billing/start-trial');
    return response.data; // { message, trial }
  },
};

// v2 (spec §13.14): reads, the stream, and the athlete's commands. Used only
// by src/v2 (every athlete is a v2 athlete). Commands answer
// 202 with the seq their first event carries; 409/422 carry the reason in
// `detail`. `command` resolves with {status, data} for every HTTP answer and
// rejects with {network: true} when there was none, so the queue can tell
// "refused" from "offline".
//
// Every read takes an optional last `{ athleteId }`: a human coach viewing a
// v2 athlete reads that athlete's calendar, thread and strategy (`?athlete_id=`,
// the backend's get_viewable_user_id). src/v2/viewing.js binds it. While a
// coach is viewing anyone, `command` refuses here, before any request: the
// coach view is read-only.
const asAthlete = (opts, params = {}) => (opts?.athleteId ? { ...params, athlete_id: opts.athleteId } : params);

export const v2API = {
  me: () => api.get('/v2/me'),
  // The athlete a coach is viewing: { user_id, coach_mode, timezone, capabilities, last_seq }, or 403.
  viewing: (athleteId) => api.get('/v2/me/viewing', { params: { athlete_id: athleteId } }),
  reportDevice: (timezone) => api.put('/v2/me/device', { timezone, platform: 'web', app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null }),
  calendar: (from, to, opts) => api.get('/v2/calendar', { params: asAthlete(opts, { from, to }) }),
  sync: (afterSeq, opts) => api.get('/v2/sync', { params: asAthlete(opts, { after_seq: afterSeq }) }),
  thread: (before = null, filter = null, opts) => api.get('/v2/thread', { params: asAthlete(opts, { ...(before ? { before } : {}), ...(filter ? { filter } : {}) }) }),
  // The chips above the composer: [{ text, context_ref, why, state }], at most 3 (v2/suggestions.js).
  suggestions: () => api.get('/v2/thread/suggestions'),
  // One session with its why: { session, intent, week, phase } (spec §13.15 session detail).
  session: (id, opts) => api.get(`/v2/sessions/${id}`, { params: asAthlete(opts) }),
  strategy: (opts) => api.get('/v2/strategy', { params: asAthlete(opts) }),
  // The athlete's active injuries: { injuries: [{ id, sports, type, max_duration_min, starts_on, review_after, content }] }.
  injuries: (opts) => api.get('/v2/injuries', { params: asAthlete(opts) }),
  // Home's goal card: { kind, metric, title, by_date, weeks_left, now, target, start, percent, achieved_at, consistency }.
  goalProgress: () => api.get('/v2/goals/progress'),
  // Sleep, HRV and resting HR from the athlete's devices, against their own normal (GET /v2/recovery, src/v2/recovery.js).
  recovery: (days = 28, opts) => api.get('/v2/recovery', { params: asAthlete(opts, { days }) }),
  openStream: (afterSeq, signal, opts) => fetchWithAuth(
    `${API_URL}/v2/stream?after_seq=${afterSeq}${opts?.athleteId ? `&athlete_id=${encodeURIComponent(opts.athleteId)}` : ''}`,
    { method: 'GET', signal, headers: { Accept: 'text/event-stream' } },
  ),
  command: async ({ method, path, body, key }) => {
    if (_viewingAthleteId) return { status: 403, data: { detail: READ_ONLY_REASON } };
    try {
      const res = await api.request({ method, url: path, data: body ?? undefined, headers: { 'Idempotency-Key': key } });
      return { status: res.status, data: res.data };
    } catch (error) {
      if (error.response) return { status: error.response.status, data: error.response.data };
      throw Object.assign(new Error(error.message || 'offline'), { network: true });
    }
  },
};

export default api;